import React, { useState, useEffect } from 'react';
import { getPrivacyPolicy } from '../../api/authApi';
import logo from '../../assets/images/logo.png';

const PrivacyPolicy = () => {
    const [policyData, setPolicyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const result = await getPrivacyPolicy();
                setPolicyData(result.data);
            } catch (err) {
                console.error('Error fetching privacy policy:', err);
                setError("Failed to load privacy policy document.");
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, []);

    const formatContent = (text) => {
        if (!text) return null;
        return text.split('\n').map((line, index) => {
            const trimmedLine = line.trim();

            if (/^\d+\./.test(trimmedLine)) {
                return (
                    <h2 key={index} className="text-slate-900 font-semibold text-xl mt-10 mb-4 tracking-tight">
                        {trimmedLine}
                    </h2>
                );
            }

            if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•')) {
                return (
                    <li key={index} className="text-slate-600 ml-6 mb-2 list-none flex items-start text-[16px] leading-7">
                        <span className="mr-2 text-slate-400">•</span>
                        {trimmedLine.replace(/^[-•]\s*/, '')}
                    </li>
                );
            }

            return trimmedLine ? (
                <p key={index} className="text-slate-600 mb-4 leading-7 text-[16px]">
                    {line}
                </p>
            ) : <div key={index} className="h-2" />;
        });
    };

    if (loading) return (
        <div className="flex justify-center items-center min-h-screen bg-white">
            <div className="w-6 h-6 border-2 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
        </div>
    );

    if (error) return (
        <div className="flex justify-center items-center min-h-screen bg-white px-6 text-center text-slate-500">
            <p>{error}</p>
        </div>
    );

    return (
        <div className="min-h-screen bg-white text-slate-900 selection:bg-blue-50">
            <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-100">
                <div className="max-w-3xl mx-auto px-6 py-4 flex justify-between items-center">
                    <span className="font-bold text-sm tracking-tight">
                        <img
                            className="img-thumbnail !p-0 !border-none !max-w-[160px] md:!max-w-[160px]"
                            src={logo}
                            alt="logo"
                        />
                    </span>
                    <span className="text-slate-400 text-xs font-medium uppercase tracking-widest">
                        Privacy Policy
                    </span>
                </div>
            </div>

            <main className="max-w-3xl mx-auto px-6 py-16 md:py-16">
                <header className="mb-12 border-b border-slate-100 pb-12">
                    <h1 className="text-4xl font-bold text-slate-900 mb-4 tracking-tight">
                        Privacy Policy
                    </h1>
                    <p className="text-slate-500 text-sm">
                        Last Updated: {policyData?.last_updated || 'January 22, 2026'}
                    </p>
                </header>

                <article className="prose prose-slate max-w-none">
                    {formatContent(policyData?.content)}
                </article>

                <footer className="mt-20 pt-8 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-slate-400 text-xs">
                        © {new Date().getFullYear()} Datara LLC. All rights reserved.
                    </p>
                    <div className="flex gap-6">
                        <button
                            onClick={() => window.print()}
                            className="text-slate-900 text-xs font-semibold hover:text-slate-600 transition-colors uppercase tracking-tight"
                        >
                            Print Document
                        </button>
                        <a href="mailto:support@datara.com" className="text-slate-900 text-xs font-semibold hover:text-slate-600 uppercase tracking-tight">
                            Contact Support
                        </a>
                    </div>
                </footer>
            </main>
        </div>
    );
};

export default PrivacyPolicy;